import React from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { MemberImage } from './MemberImage';
import { MemberHealth, MemberHealthControl } from './MemberHealthControl';
import { MiniCardExpand } from './MiniCardExpand';
import { createRivetedPanelSurface, hashStringToSeed } from '../../theme/surfaces';

interface MemberMiniCardProps {
  warbandId: string;
  memberId: string;
  name: string;
  subtitle?: string;
  accessToken: string | null;
  editMode?: boolean;
  health?: MemberHealth;
  /** Compact one-line stat readout, e.g. [{ label: 'M', value: '6' }]. */
  stats?: Array<{ label: string; value: string }>;
  statusContent?: React.ReactNode;
  opacity?: number;
  overlay?: React.ReactNode;
  /** Full member card shown in a modal via the expand button. */
  fullCard: React.ReactNode;
}

export const MemberMiniCard: React.FC<MemberMiniCardProps> = ({
  warbandId,
  memberId,
  name,
  subtitle,
  accessToken,
  editMode = false,
  health,
  stats,
  statusContent,
  opacity,
  overlay,
  fullCard,
}) => {
  // Stargrave (dark theme): vary the riveted texture per member.
  const isDark = useTheme().palette.mode === 'dark';
  const memberSurfaceSx = React.useMemo(
    () => (isDark ? createRivetedPanelSurface(hashStringToSeed(memberId)) : {}),
    [isDark, memberId]
  );
  return (
    <Paper variant="outlined" sx={{ p: 1.5, width: 280, minHeight: 148, opacity, position: 'relative', ...memberSurfaceSx }}>
      {overlay}
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ position: 'relative', zIndex: 3 }}>
        <MemberImage warbandId={warbandId} memberId={memberId} editMode={editMode} minified accessToken={accessToken} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{name}</Typography>
            {statusContent}
            <MiniCardExpand label={name} fullCard={fullCard} />
          </Stack>
          {subtitle && <Typography variant="caption" color="text.secondary" noWrap component="div">{subtitle}</Typography>}
          {stats && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {stats.map((stat) => (
                <Typography key={stat.label} variant="caption" sx={{ fontWeight: 700 }}>
                  {stat.label}&thinsp;<Box component="span" sx={{ fontWeight: 400 }}>{stat.value}</Box>
                </Typography>
              ))}
            </Stack>
          )}
          {health && <Box sx={{ mt: 1 }}><MemberHealthControl {...health} /></Box>}
        </Box>
      </Stack>
    </Paper>
  );
};
